import React from 'react';
import { motion } from 'framer-motion';
import { FaLaptopCode, FaMobileAlt, FaServer, FaCogs, FaPalette, FaDatabase, FaCloud, FaShieldAlt } from 'react-icons/fa';

const ServicesPage = () => {
  const services = [
    {
      id: 1,
      title: 'Web Development',
      description: 'Custom web applications built with the latest technologies to deliver exceptional user experiences.',
      icon: <FaLaptopCode className="text-primary-500" size={40} />,
      details: [
        'Responsive website development',
        'Progressive Web Apps (PWAs)',
        'E-commerce platforms',
        'Content Management Systems',
        'Custom web portals and dashboards'
      ]
    },
    {
      id: 2,
      title: 'Mobile App Development',
      description: 'Native and cross-platform mobile applications for iOS and Android that engage and delight users.',
      icon: <FaMobileAlt className="text-primary-500" size={40} />,
      details: [
        'iOS app development',
        'Android app development',
        'Cross-platform solutions (React Native, Flutter)',
        'App maintenance and updates',
        'App Store optimization'
      ]
    },
    {
      id: 3,
      title: 'Enterprise Solutions',
      description: 'Scalable and secure enterprise software solutions that streamline operations and boost productivity.',
      icon: <FaServer className="text-primary-500" size={40} />,
      details: [
        'Enterprise Resource Planning (ERP)',
        'Customer Relationship Management (CRM)',
        'Business Intelligence solutions',
        'Workflow automation',
        'Legacy system integration'
      ]
    },
    {
      id: 4,
      title: 'Custom Software',
      description: 'Tailor-made software solutions designed to address your unique business challenges and requirements.',
      icon: <FaCogs className="text-primary-500" size={40} />,
      details: [
        'Custom software architecture',
        'Bespoke application development',
        'Software modernization',
        'API development and integration',
        'Microservices implementation'
      ]
    },
    {
      id: 5,
      title: 'UI/UX Design',
      description: 'Intuitive and visually appealing interfaces that enhance user engagement and satisfaction.',
      icon: <FaPalette className="text-primary-500" size={40} />,
      details: [
        'User research and personas',
        'Wireframing and prototyping',
        'Visual design and branding',
        'Usability testing',
        'Responsive design systems'
      ]
    },
    {
      id: 6,
      title: 'Database Solutions',
      description: 'Robust database design, optimization, and management for efficient data storage and retrieval.',
      icon: <FaDatabase className="text-primary-500" size={40} />,
      details: [
        'Database architecture and design',
        'Data migration and integration',
        'Performance optimization',
        'NoSQL and SQL solutions',
        'Data warehousing'
      ]
    },
    {
      id: 7,
      title: 'Cloud Services',
      description: 'Leverage the power of cloud computing for scalable, reliable, and cost-effective solutions.',
      icon: <FaCloud className="text-primary-500" size={40} />,
      details: [
        'Cloud migration strategies',
        'AWS, Azure, and Google Cloud solutions',
        'Serverless architecture',
        'DevOps implementation',
        'Continuous Integration/Continuous Deployment'
      ]
    },
    {
      id: 8,
      title: 'Cybersecurity',
      description: 'Protect your digital assets with comprehensive security measures and best practices.',
      icon: <FaShieldAlt className="text-primary-500" size={40} />,
      details: [
        'Security assessment and auditing',
        'Secure application development',
        'Authentication and authorization systems',
        'Data encryption',
        'Compliance (GDPR, HIPAA, etc.)'
      ]
    }
  ];

  const process = [
    {
      id: 1,
      title: 'Discovery',
      description: 'We start by understanding your business goals, target audience, and project requirements through in-depth consultations.'
    },
    {
      id: 2,
      title: 'Planning',
      description: 'Our team creates a detailed project roadmap, including timelines, milestones, and resource allocation.'
    },
    {
      id: 3,
      title: 'Design',
      description: 'We develop wireframes and prototypes to visualize the solution before moving to development.'
    },
    {
      id: 4,
      title: 'Development',
      description: 'Our developers write clean, efficient code following industry best practices and your specific requirements.'
    },
    {
      id: 5,
      title: 'Testing',
      description: 'Rigorous quality assurance testing ensures your solution is bug-free and performs optimally.'
    },
    {
      id: 6,
      title: 'Deployment',
      description: 'We handle the deployment process, ensuring a smooth transition to production.'
    },
    {
      id: 7,
      title: 'Support',
      description: 'Our relationship continues after launch with ongoing maintenance and support services.'
    }
  ];

  return (
    <>
      {/* Hero Section */}
      <section className="relative py-20 bg-gradient-to-r from-secondary-900 to-secondary-800 text-white">
        <div className="absolute inset-0 bg-black opacity-50"></div>
        <div className="container relative">
          <div className="max-w-3xl mx-auto text-center">
            <motion.h1 
              className="mb-6 text-4xl font-bold md:text-5xl"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5 }}
            >
              Our Services
            </motion.h1>
            <motion.p 
              className="text-lg text-secondary-200"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.2 }}
            >
              We offer a comprehensive range of software development services to help businesses thrive in the digital age.
            </motion.p>
          </div>
        </div>
      </section>

      {/* Services Section */}
      <section className="section">
        <div className="container">
          <div className="mb-12 text-center">
            <h2 className="section-title">What We Offer</h2>
            <p className="max-w-2xl mx-auto text-lg text-secondary-600">
              Our expertise spans across various domains of software development, allowing us to deliver end-to-end solutions for businesses of all sizes.
            </p>
          </div>

          <div className="grid grid-cols-1 gap-8 md:grid-cols-2 lg:grid-cols-3">
            {services.map((service) => (
              <motion.div 
                key={service.id} 
                className="p-6 bg-white rounded-lg shadow-md hover:shadow-lg transition-shadow"
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5 }}
                viewport={{ once: true }}
              >
                <div className="flex items-center justify-center w-16 h-16 mb-4 bg-primary-50 rounded-lg">
                  {service.icon}
                </div>
                <h3 className="mb-3 text-xl font-semibold">{service.title}</h3>
                <p className="mb-4 text-secondary-600">{service.description}</p>
                <ul className="pl-5 mt-4 space-y-2 list-disc text-secondary-700">
                  {service.details.map((detail, index) => (
                    <li key={index}>{detail}</li>
                  ))}
                </ul>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Our Process Section */}
      <section className="section bg-secondary-50">
        <div className="container">
          <div className="mb-12 text-center">
            <h2 className="section-title">Our Development Process</h2>
            <p className="max-w-2xl mx-auto text-lg text-secondary-600">
              We follow a structured approach to ensure the successful delivery of every project.
            </p>
          </div>

          <div className="relative">
            {/* Process Timeline */}
            <div className="absolute left-1/2 top-0 bottom-0 w-1 bg-primary-200 transform -translate-x-1/2 hidden md:block"></div>
            
            <div className="space-y-12">
              {process.map((step, index) => (
                <motion.div 
                  key={step.id}
                  className="relative"
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.5 }}
                  viewport={{ once: true }}
                >
                  <div className={`flex flex-col items-center md:flex-row ${index % 2 === 0 ? 'md:flex-row-reverse' : ''}`}>
                    {/* Timeline Dot */}
                    <div className="absolute left-1/2 w-6 h-6 bg-primary-500 rounded-full transform -translate-x-1/2 hidden md:block"></div>
                    
                    {/* Content */}
                    <div className="w-full p-6 bg-white rounded-lg shadow-md md:w-5/12">
                      <div className="flex items-center mb-4">
                        <div className="flex items-center justify-center w-10 h-10 mr-4 text-white bg-primary-500 rounded-full">
                          {step.id}
                        </div>
                        <h3 className="text-xl font-semibold">{step.title}</h3>
                      </div>
                      <p className="text-secondary-600">{step.description}</p>
                    </div>
                  </div>
                </motion.div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 bg-primary-600 text-white">
        <div className="container">
          <div className="max-w-3xl mx-auto text-center">
            <h2 className="mb-6 text-3xl font-bold">Ready to Start Your Project?</h2>
            <p className="mb-8 text-lg">
              Contact us today to discuss how we can help bring your ideas to life.
            </p>
            <a 
              href="/contact" 
              className="px-8 py-3 text-primary-600 bg-white rounded-md hover:bg-secondary-100 transition-colors"
            >
              Get in Touch
            </a>
          </div>
        </div>
      </section>
    </>
  );
};

export default ServicesPage;
